import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StageScreen, StagePopup } from './components/StageShell';
import { s3PublicUrl } from '../../utils/s3PublicUrl';

// The map is loaded with fetch()+text() (not a plain <img>) so its <path>
// elements can get per-country click handlers via dangerouslySetInnerHTML —
// that requires the response to actually pass CORS, unlike every other theme
// asset here which just renders in an <img> tag (browsers don't enforce CORS
// for that). The S3 bucket's CORS config only allowlists specific origins,
// so players on an org/domain not in that allowlist get a CORS failure here
// ("Failed to load resource: net::ERR_FAILED" / "Map could not be loaded")
// even though the file itself loads fine for an allowlisted origin. Route
// through the backend's /themeAsset proxy instead — it sets
// Access-Control-Allow-Origin: '*' specifically so this works from any origin.
function resolveIntroMapUrl(mapSvg) {
  const s = String(mapSvg ?? '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;

  const backend = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  if (!backend) return s3PublicUrl(mapSvg);

  const key = s.replace(/^\/+/, '');
  return `${backend}/themeAsset?key=${encodeURIComponent(key)}`;
}

function popupHasContent(image, text) {
  return Boolean(String(image ?? '').trim()) || Boolean(String(text ?? '').trim());
}

export default function IntroPage({ intro, pagesBg, themeBg, onComplete }) {
  const hasPopup = popupHasContent(intro?.start_popup_image, intro?.start_popup_text);

  useEffect(() => {
    if (!hasPopup) onComplete();
  }, [hasPopup, onComplete]);

  if (!hasPopup) return null;

  // Uses the intro_map background image (never a video) — same background
  // the following map-pin screen shows, so the two feel like one continuous
  // screen rather than a hard cut.
  return (
    <StageScreen pagesBg={pagesBg} pageKey="intro_map" stage={themeBg}>
      <StagePopup
        image={intro?.start_popup_image}
        text={intro?.start_popup_text}
        nextButtonImage={intro?.next_button_image}
        onNext={onComplete}
      />
    </StageScreen>
  );
}

function getPinPosition(mapEl, country, zoom) {
  if (!mapEl || !country) return null;
  const path = mapEl.querySelector(`path[data-country="${CSS.escape(country)}"]`);
  if (!path) return null;
  const mapRect = mapEl.getBoundingClientRect();
  const rect = path.getBoundingClientRect();
  const z = zoom || 1;
  return {
    left: (rect.left - mapRect.left + rect.width / 2) / z,
    top: (rect.top - mapRect.top + rect.height / 2) / z,
  };
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

export function IntroMapPage({ intro, pagesBg, onComplete }) {
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [svgMarkup, setSvgMarkup] = useState('');
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState('');
  const [pinLayout, setPinLayout] = useState({});
  const [heading1Failed, setHeading1Failed] = useState(false);
  const [heading2Failed, setHeading2Failed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const mapRef = useRef(null);
  const maxPins = Math.min(5, Math.max(1, Number(intro?.max_pins) || 4));
  const mapUrl = resolveIntroMapUrl(intro?.map_svg);

  const heading1Url = s3PublicUrl(intro?.heading1_image);
  const heading2Url = s3PublicUrl(intro?.heading2_image);
  const pinImg = s3PublicUrl(intro?.pin_image);

  const showHeading1Img = Boolean(heading1Url) && !heading1Failed;
  const showHeading2Img = Boolean(heading2Url) && !heading2Failed;

  const headingLines = useMemo(() => {
    const lines = [
      intro?.intro_line1,
      intro?.intro_line2,
      intro?.intro_line3,
    ].filter((l) => String(l ?? '').trim());
    const question = String(intro?.intro_question ?? '').trim();
    return { lines, question };
  }, [intro]);

  useEffect(() => {
    if (!mapUrl) {
      setSvgMarkup('');
      setMapError('missing');
      return undefined;
    }
    let cancelled = false;
    setMapLoading(true);
    setMapError('');
    fetch(mapUrl)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (!cancelled) setSvgMarkup(text);
      })
      .catch((err) => {
        if (!cancelled) {
          setSvgMarkup('');
          setMapError('load');
          console.warn('[IntroMap] failed to load map SVG', mapUrl, err);
        }
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mapUrl]);

  const refreshPinLayout = useCallback(() => {
    const mapEl = mapRef.current;
    if (!mapEl) return;
    const next = {};
    selectedCountries.forEach((country) => {
      const pos = getPinPosition(mapEl, country, zoom);
      if (pos) next[country] = pos;
    });
    setPinLayout(next);
  }, [selectedCountries, zoom]);

  useEffect(() => {
    refreshPinLayout();
  }, [refreshPinLayout, svgMarkup, zoom]);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  }, []);

  useEffect(() => {
    if (!mapRef.current || !svgMarkup) return undefined;
    const mapEl = mapRef.current;

    const handler = (e) => {
      const path = e.target.closest('path[data-country]');
      if (!path) return;
      const country = path.getAttribute('data-country');
      if (!country) return;
      setSelectedCountries((prev) => {
        if (prev.length >= maxPins && !prev.includes(country)) return prev;
        if (prev.includes(country)) return prev;
        return [...prev, country];
      });
    };

    mapEl.addEventListener('click', handler);
    return () => mapEl.removeEventListener('click', handler);
  }, [svgMarkup, maxPins]);

  useEffect(() => {
    const onResize = () => refreshPinLayout();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [refreshPinLayout]);

  useEffect(() => {
    if (selectedCountries.length >= maxPins) {
      const t = setTimeout(() => onComplete(selectedCountries), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [selectedCountries, maxPins, onComplete]);

  return (
    <StageScreen pagesBg={pagesBg} pageKey="intro_map">
      <div className="fi-intro-map-root">
        <div className="fi-intro-map-headings">
          {showHeading1Img ? (
            <img
              className="fi-intro-map-heading"
              src={heading1Url}
              alt=""
              onError={() => setHeading1Failed(true)}
            />
          ) : null}
          {showHeading2Img ? (
            <img
              className="fi-intro-map-heading"
              src={heading2Url}
              alt=""
              onError={() => setHeading2Failed(true)}
            />
          ) : null}
          {!showHeading1Img && !showHeading2Img ? (
            <div className="fi-intro-map-copy">
              {headingLines.lines.map((line) => (
                <p key={line} className="fi-intro-map-line">
                  {line}
                </p>
              ))}
              {headingLines.question ? (
                <p className="fi-intro-map-question">{headingLines.question}</p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="fi-intro-map-stage">
          <div className="fi-intro-map-wrap">
            <div className="fi-intro-map-scroll">
              <div
                className="fi-intro-map-zoom-inner"
                ref={mapRef}
                style={{ transform: `scale(${zoom})` }}
              >
                {mapLoading ? (
                  <p className="fi-intro-map-missing">Loading map…</p>
                ) : svgMarkup ? (
                  <div
                    className="fi-intro-map-svg"
                    dangerouslySetInnerHTML={{ __html: svgMarkup }}
                  />
                ) : (
                  <p className="fi-intro-map-missing">
                    {mapError === 'missing'
                      ? 'Map not configured in theme.'
                      : 'Map could not be loaded. Upload the map SVG in admin.'}
                  </p>
                )}

                <div className="fi-intro-pins-layer" aria-hidden>
                  {selectedCountries.map((country) => {
                    const pos = pinLayout[country];
                    if (!pos) return null;
                    return (
                      <div
                        key={country}
                        className="fi-intro-pin-marker"
                        style={{
                          left: pos.left,
                          top: pos.top,
                          backgroundImage: pinImg ? `url(${pinImg})` : undefined,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="fi-intro-pin-hint fi-intro-pin-hint--text">
              Pin the red-colored drawing pin to lock in your prediction.{' '}
              <strong>You can select multiple options</strong>
            </div>

            {pinImg ? (
              <img className="fi-intro-pin-sample" src={pinImg} alt="" aria-hidden />
            ) : null}

            <div className="fi-intro-map-zoom-controls">
              <button
                type="button"
                className="fi-intro-map-zoom-btn"
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                className="fi-intro-map-zoom-btn"
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                aria-label="Zoom in"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </StageScreen>
  );
}
